import * as Yup from 'yup';

import { billingTypeOptions, isInvoiceBillingType } from '~constants/maps';

import { Column, ColumnType, Context, Row } from '~components/table-view-v3';
import { selectEditor } from '~components/table-view-v3/columnEditors';
import ReadOnlyCategory from '~support-at-home/components/config/price-plans/ReadOnlyCategory';
import ReadOnlyService from '~support-at-home/components/config/price-plans/ReadOnlyService';

import { Lookup, SahCategory, SahServiceTypeAssociation } from '~lookups/models';
import { PricePlanModalRow } from '~support-at-home/models';

import { ServiceTypeRow } from '~appointments/reducers/serviceTypes';

const isEditable = (row: Row, context?: Context) => {
  // blanket check for all applicable columns when editing
  if (!context?.initialRows) return true;
  const rows = context.initialRows as PricePlanModalRow[];
  return !rows.find(({ id }) => id === row.id);
};

export const columnsFactory = (
  categories: SahCategory[],
  serviceTypes: ServiceTypeRow[],
  serviceTypeAssocs: SahServiceTypeAssociation[],
  shifts: Lookup[],
): Column[] => {
  const categoryOptions = categories.map(({ id, name }) => ({ id, name }));
  const billingOptions = billingTypeOptions.map(({ value, label }) => ({ id: value, name: label }));

  const onCategoryChange = (row: any, context: any) => {
    const { id, category_id, service_type_id } = row;
    const validServiceTypes = getServiceTypesForCategory(
      id,
      category_id,
      serviceTypeAssocs,
      serviceTypes,
      context?.getAllRows?.() ?? [],
    );

    if (!validServiceTypes.some((st) => st.id === service_type_id)) {
      return { service_type_id: undefined };
    }

    return { service_type_id };
  };

  const getServiceTypesForCategory = (
    id: string | number,
    category_id: string,
    serviceTypeAssocs: SahServiceTypeAssociation[],
    serviceTypes: ServiceTypeRow[],
    allRows: any,
  ) => {
    const serviceTypeIds = serviceTypeAssocs
      .filter((assoc) => assoc.category_id === category_id)
      .flatMap((assoc) => assoc.service_type_ids);

    const allocatedServiceTypeIds = allRows
      .filter((row: any) => row.category_id === category_id && row.id !== id)
      .map((row: any) => row.service_type_id);

    return serviceTypes.filter(({ id }) => serviceTypeIds.includes(id) && !allocatedServiceTypeIds.includes(id));
  };

  const clearShiftValues = (row: any, shifts: Lookup[]) => {
    if (isInvoiceBillingType(row.billing_type)) {
      const clearedShifts: Record<string, any> = {};

      shifts.forEach(({ id }) => {
        clearedShifts[id] = undefined;
      });

      return clearedShifts;
    }
    return {};
  };

  const columns: Column[] = [
    {
      id: 'category_id',
      header: 'Category',
      type: 'select',
      style: { width: '175px' },
      options: categoryOptions,
      onChange: onCategoryChange,
      isEditable,
      renderReadOnly: (_value, row) => ReadOnlyCategory({ row, serviceTypeAssocs, categories }),
      validation: Yup.string().required('Category is required'),
    },
    {
      id: 'service_type_id',
      header: 'Service Type',
      type: 'select',
      style: { minWidth: '300px' },
      options: serviceTypes,
      isEditable,
      renderEditor: selectEditor(
        (row, context) =>
          getServiceTypesForCategory(
            row.id,
            row.category_id,
            serviceTypeAssocs,
            serviceTypes,
            context?.getAllRows?.() ?? [],
          ),
        true,
      ),
      renderReadOnly: (_value, row) => ReadOnlyService({ row, serviceTypes }),
      validation: Yup.number().required('Service Type is required'),
    },
    {
      id: 'billing_type',
      header: 'Billing Type',
      type: 'select',
      style: { width: '160px' },
      options: billingOptions,
      onChange: (row) => clearShiftValues(row, shifts),
      validation: Yup.string().required('Billing Type is required'),
    },
    ...shifts.map(({ id, name }) => ({
      id,
      header: name,
      type: 'currency' as ColumnType,
      width: '120px',
      isEditable: (row: Row) => row.billing_type && !isInvoiceBillingType(row.billing_type),
      validation: Yup.number().typeError('Cost must be a number').min(0, 'Cost cannot be negative').nullable(),
    })),
  ];

  return columns;
};
